import { Injectable, inject } from '@angular/core';
import { IDBPDatabase, IDBPCursorWithValue } from 'idb';

import { SearchResponseModel } from '../models/search-response.model';
import { RepositoryConfigModel } from '../models/repository-config.model';
import { RepositoryService } from '../../services/repository.service';
import { StaticReferences } from '../utils/static-references';
import { CrudOperationEnum } from '../../models/enums/crud-operation.enum';
import { UnsyncedChangeModel } from '../../models/unsynced-change.model';
import { BaseEntityModel } from './base-entity.model';
import { Tools } from '../utils/tools';

@Injectable()
export abstract class BaseRepository<T extends BaseEntityModel> {
    private repositoryService = inject(RepositoryService);

    protected config: RepositoryConfigModel;
    protected database: IDBPDatabase;
    protected skipLocalUpdate = false;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {
        if (!('indexedDB' in window)) {
            throw new Error(`This browser doesn't support IndexedDB`);
        }
        this.repositoryService.databaseSubject.subscribe(db => {
            this.database = db;
        }
        );

    }

    async getAll(query?: IDBKeyRange, maxResults?: number): Promise<T[]> {
        return this.database.getAll(this.config.tableName, query, maxResults);
    }

    async getById(id: string): Promise<T> {
        return this.database.get(this.config.tableName, id);
    }

    async count(): Promise<number> {
        return this.database.count(this.config.tableName);
    }

    async getCursor(
        query?: string | number | Date | ArrayBufferView | ArrayBuffer | IDBKeyRange,
        direction?: IDBCursorDirection
    ): Promise<IDBPCursorWithValue<unknown, [ string ], string, unknown>> {
        //@ts-ignore
        return this.database.transaction(this.config.tableName).store.openCursor(query, direction);
    }

    async getAllForIndex(value: string): Promise<T[]> {
        const index = this.database.transaction(this.config.tableName).store.index(this.config.index);
        return index.getAll(IDBKeyRange.only(value));
    }

    abstract sync(data: SearchResponseModel<T>): Promise<void>;

    protected async markAsChangeRange(operation: CrudOperationEnum, records?: T[], recordsIds?: string[]): Promise<void> {
        if (this.skipLocalUpdate) {
            return;
        }

        if (records) {
            for (const record of records) {
                await this.markAsChange(operation, record);
            }
        }

        if (recordsIds) {
            for (const recordId of recordsIds) {
                await this.markAsChange(operation, undefined, recordId);
            }
        }
    }

    protected async markAsChange(operation: CrudOperationEnum, record?: T, recordId?: string): Promise<void> {
        if (this.skipLocalUpdate) {
            return;
        }
        let existingEntries = [];
        if (record) {
            existingEntries = await StaticReferences.unsyncedChangesRepository
                .getByRecordId(this.config.tableName, record.id);

            existingEntries = existingEntries.sort((a, b) => a.date.getTime() - b.date.getTime());
        }

        await this.updateModifiedDate();
        if (existingEntries.length < 1) {
            await this.addChange(operation, record, recordId);
        } else {
            if (existingEntries.length === 1) {
                const previousEntry = existingEntries[ 0 ];

                // If the previous operation and the new one are Update, we maintain only the last.
                if (previousEntry.operation === operation && operation === CrudOperationEnum.Update) {
                    await StaticReferences.unsyncedChangesRepository.delete(previousEntry.id);
                    await this.addChange(operation, record, recordId);
                } else if (previousEntry.operation !== operation) {
                    // Since it's not the first operation, it can't be Create.
                    switch (operation) {
                        case CrudOperationEnum.Update:
                            // If Update, the previous can only be Create.
                            // So we just let Create as type with the updated data.
                            previousEntry.record = record;
                            await StaticReferences.unsyncedChangesRepository.edit(previousEntry);
                            break;
                        case CrudOperationEnum.Delete:
                            // If Delete, we always remove the previous.
                            // If the previous was Create, we just remove everything.
                            // Since it was created and deleted only locally, there's nothing to sync.
                            // If the previous was Update, we remove the Update and leave only Delete.
                            await StaticReferences.unsyncedChangesRepository.delete(previousEntry.id);

                            // If the previous was Update, we add Delete.
                            if (previousEntry.operation === CrudOperationEnum.Update) {
                                await this.addChange(operation, record, recordId);
                            }

                            break;
                    }
                }
            } else {
                // TODO: If we have more than one record with the same ID, check all pending operations on that ID
                // TODO: check if we still need this now that sync happens more often. But it's likely we'll need.
            }
        }
    }

    private async updateModifiedDate(): Promise<void> {
        const tableState = await StaticReferences.tableStateRepository.getById(this.config.tableName);
        if (!tableState) {
            return;
        }

        const now = new Date();
        const nowUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
            now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds());
        tableState.lastLocalUpdate = new Date(nowUtc);

        await StaticReferences.tableStateRepository.edit(tableState);
    }

    private async addChange(operation: CrudOperationEnum, record?: T, recordId?: string): Promise<void> {

        await StaticReferences.unsyncedChangesRepository.add(new UnsyncedChangeModel({
            tableName: this.config.tableName,
            operation,
            recordId: record ? record.id.toString() : recordId.toString(),
            record
        }));
    }
}
